import { PencilIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useRevokeIndividualAccess } from '@/api/hooks/useRevokeIndividualAccess'
import { relationLabelKey } from '@/components/molecules/relation-select'
import { ConfirmDialog } from '@/components/organisms/confirm-dialog'
import { Button } from '@/components/ui/button'
import { IndividualType } from '@/hooks/useIndividuals'

import { EditOwnerRelationDialog } from './edit-owner-relation-dialog'

type Owner = NonNullable<IndividualType['users']>[number]

type Props = {
  individualId: string
  /** Admin-only actions: editing another user's relation and revoking access. */
  canManage: boolean
  owners: Owner[]
}

/** Compact, separated list of co-owners — name + relation, with admin-only actions. */
export const OwnersList = ({ canManage, individualId, owners }: Props) => {
  return (
    <ul className="divide-y divide-subtle-01 overflow-hidden rounded-xl border border-subtle-01 bg-layer-1">
      {owners.map(owner => (
        <OwnerRow canManage={canManage} individualId={individualId} key={owner.id} owner={owner} />
      ))}
    </ul>
  )
}

const OwnerRow = ({
  canManage,
  individualId,
  owner,
}: {
  individualId: string
  canManage: boolean
  owner: Owner
}) => {
  const { t } = useTranslation()
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [editOpen, setEditOpen] = useState(false)
  const { mutateAsync: revokeAccess } = useRevokeIndividualAccess()

  const pidLabel = `${owner.pid}-xxxx`
  const hasName = !!owner.name?.trim()
  // Guardian / legal-representative are part of the relation the admin edits,
  // so show them here too — otherwise those edits leave no visible trace.
  const relationLabel = [
    t(relationLabelKey(owner.relation, 'third-person')),
    owner.isGuardian ? t('relation.isGuardian.label') : null,
    owner.isLegalRepresentative ? t('relation.isLegalRepresentative.label') : null,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <li className="flex items-center justify-between gap-3 px-4 py-2.5">
      <div className="min-w-0">
        <p className="truncate font-medium">{hasName ? owner.name : pidLabel}</p>
        <p className="text-text-secondary text-base">
          {hasName ? `${relationLabel} – (${pidLabel})` : relationLabel}
        </p>
      </div>
      {canManage && (
        <div className="flex shrink-0 items-center gap-1">
          <Button
            aria-label={t('editRelationDialog.title')}
            className="size-8"
            onClick={() => setEditOpen(true)}
            size="icon"
            variant="ghost"
          >
            <PencilIcon />
          </Button>
          <Button
            aria-label={t('usersTable.rowActionsDropdown.revokeAccess')}
            className="size-8 text-error"
            onClick={() => setConfirmOpen(true)}
            size="icon"
            variant="ghost"
          >
            <Trash2Icon />
          </Button>
          <EditOwnerRelationDialog
            individualId={individualId}
            onOpenChange={setEditOpen}
            open={editOpen}
            owner={owner}
          />
          <ConfirmDialog
            onConfirm={() => revokeAccess({ individualId, requestBody: { userId: owner.id } })}
            onOpenChange={setConfirmOpen}
            open={confirmOpen}
            variant="destructive"
          />
        </div>
      )}
    </li>
  )
}

export default OwnersList
